/**
 * Swarms: touching cells with something going on, as the map's pulse draws them (amendment 14).
 *
 * Purely a reading of numbers the map already has. A swarm changes no GDI, no vibe and no
 * alert: brief §8 scores every cell on its own, and two busy neighbours are two busy cells, not
 * one busier one. What a swarm adds is the order in which a pulse reaches its members, and that
 * order is all this module works out.
 *
 * Pure like the rest of the directory, and indifferent to the order the cells arrive in: a
 * refetch that returns the same cells in a different order must not start the wave somewhere
 * else.
 */

import type { Cell } from './h3.ts';
import { neighbours } from './h3.ts';

/**
 * The first step of the colour scale that is not grey (brief §14: "20–40 gelb").
 *
 * A grey cell has reports but nothing going on, and letting it join would grow swarms through
 * quiet streets.
 */
export const SWARM_MIN_GDI = 20;

export type SwarmCell = { cell: Cell; gdi: number };

export type SwarmMember = SwarmCell & {
  /** Steps from the origin through the swarm; 0 for the origin itself. */
  hop: number;
};

export type SwarmLink = {
  /** The member the pulse leaves from: the one nearer the origin. */
  from: Cell;
  to: Cell;
  /**
   * Whether the pulse runs along this link. Two members at the same distance from the origin
   * are reached at the same moment, so nothing travels between them; the link still belongs to
   * the swarm and is drawn, it just carries no light.
   */
  carries: boolean;
};

export type Swarm = {
  /** The hottest member, where every pulse starts. */
  origin: Cell;
  /** Nearest the origin first. */
  members: SwarmMember[];
  links: SwarmLink[];
  /** Σ GDI of the members: how strongly the swarm pulses, never a number anybody is shown. */
  total: number;
};

/**
 * The swarms among these cells: two or more touching cells, each at `SWARM_MIN_GDI` or above.
 *
 * "Touching" is H3 adjacency at whatever resolution the cells have, so the res-7 overview works
 * as well as the res-9 cells, and the two are never mixed because the API sends one at a time.
 * A cell on its own is not a swarm: there is nobody to pass a pulse to.
 */
export function swarmsOf(cells: readonly SwarmCell[]): Swarm[] {
  const active = new Map<Cell, number>();
  for (const { cell, gdi } of cells) {
    if (gdi >= SWARM_MIN_GDI) active.set(cell, gdi);
  }

  // Sorted here and below, so that nothing depends on the order the API happened to use.
  const order = [...active.keys()].sort();
  const adjacent = new Map<Cell, Cell[]>(
    order.map((cell) => [
      cell,
      neighbours(cell, 1)
        .filter((other) => other !== cell && active.has(other))
        .sort(),
    ]),
  );

  const placed = new Set<Cell>();
  const swarms: Swarm[] = [];
  for (const start of order) {
    if (placed.has(start)) continue;

    const group: Cell[] = [];
    const stack = [start];
    placed.add(start);
    for (let cell = stack.pop(); cell !== undefined; cell = stack.pop()) {
      group.push(cell);
      for (const next of adjacent.get(cell) ?? []) {
        if (placed.has(next)) continue;
        placed.add(next);
        stack.push(next);
      }
    }
    if (group.length < 2) continue;

    const gdiOf = (cell: Cell) => active.get(cell) ?? 0;

    // Hottest first, and a tie goes to the lower index, so that it is broken the same way on
    // every device and after every refetch.
    let origin = start;
    for (const cell of group) {
      const better =
        gdiOf(cell) > gdiOf(origin) || (gdiOf(cell) === gdiOf(origin) && cell < origin);
      if (better) origin = cell;
    }

    // Breadth-first from the origin: a member's hop is its distance through the swarm, which
    // is not always its distance on the map — a swarm shaped like a horseshoe carries the
    // pulse round the bend, not across the gap.
    const hops = new Map<Cell, number>([[origin, 0]]);
    const queue = [origin];
    for (let index = 0; index < queue.length; index += 1) {
      const cell = queue[index];
      if (cell === undefined) break;
      const hop = hops.get(cell) ?? 0;
      for (const next of adjacent.get(cell) ?? []) {
        if (hops.has(next)) continue;
        hops.set(next, hop + 1);
        queue.push(next);
      }
    }
    const hopOf = (cell: Cell) => hops.get(cell) ?? 0;

    const members = group
      .map((cell) => ({ cell, gdi: gdiOf(cell), hop: hopOf(cell) }))
      .sort((a, b) => a.hop - b.hop || (a.cell < b.cell ? -1 : 1));

    const links: SwarmLink[] = [];
    for (const { cell } of members) {
      for (const other of adjacent.get(cell) ?? []) {
        // Each pair once, and pointing away from the origin.
        if (hopOf(other) < hopOf(cell) || (hopOf(other) === hopOf(cell) && other < cell)) continue;
        links.push({ from: cell, to: other, carries: hopOf(other) !== hopOf(cell) });
      }
    }

    swarms.push({
      origin,
      members,
      links,
      total: members.reduce((sum, member) => sum + member.gdi, 0),
    });
  }
  return swarms;
}
